/* eslint-disable max-lines */
import { ChevronLeftIcon } from "@heroicons/react/24/outline";
import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { Link, useNavigate, useParams } from "react-router-dom";
import * as yup from "yup";
import Button from "../../../../components/ui/Button";
import FormWrapper from "../../../../components/ui/FormWrapper";
import InfoTip from "../../../../components/ui/InfoTip";
import Input from "../../../../components/ui/input/Input";
import SpinnerLoader from "../../../../components/ui/SpinnerLoader";
import { IGTFSStation } from "../../../../interfaces/endpoints/IGTFS.types";
import { getGTFSStation, resetGTFSStationToStation, updateGTFSStation } from "../../../../services/GTFS.service";

const schema = yup.object().shape({
  name: yup.string().required("Emri është i detyrueshëm"),
  latitude: yup.number().required("Gjerësia është e detyrueshme")
    .min(-90, "Gjerësia duhet të jetë më e madhe se -90")
    .max(90, "Gjerësia duhet të jetë më e vogël se 90"),
  longitude: yup.number().required("Gjatësia është e detyrueshme")
    .min(-180, "Gjatësia duhet të jetë më e madhe se -180")
    .max(180, "Gjatësia duhet të jetë më e vogël se 180")
});

const STATION_SOURCE_TIP =
  "Emri dhe vendndodhja merren nga stacioni, përveç fushave që ndryshohen këtu. " +
  "Një fushë e ndryshuar këtu nuk ndjek më ndryshimet e stacionit.";

const EditGTFSStation: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isResetting, setIsResetting] = useState(false);
  const [flags, setFlags] = useState({ isNameEdited: false, isLocationEdited: false });

  const {
    register,
    handleSubmit,
    reset,
    resetField,
    formState: { errors, isSubmitting, isSubmitSuccessful, dirtyFields }
  } = useForm<IGTFSStation>({
    resolver: yupResolver(schema),
    mode: "all",
    defaultValues: {
      id: undefined,
      name: "",
      latitude: undefined,
      longitude: undefined
    }
  });

  useEffect(() => {
    const loadStation = async () => {
      try {
        setIsLoading(true);
        const response = await getGTFSStation(Number(id));
        reset(response.data);
        setFlags({
          isNameEdited: !!response.data.isNameEdited,
          isLocationEdited: !!response.data.isLocationEdited
        });
      } catch (error) {
        toast.error("Ngarkimi i stacionit dështoi");
        navigate("/gtfs/stations");
      } finally {
        setIsLoading(false);
      }
    };

    if (id) {
      void loadStation();
    }
  }, [id, navigate, reset]);

  // Makes one field follow the station again. Only that field takes the station's value, so unsaved
  // edits to the other field stay in the form.
  const onResetToStation = async (field: "name" | "location") => {
    try {
      setIsResetting(true);
      const response = await resetGTFSStationToStation(Number(id), {
        name: field === "name",
        location: field === "location"
      });

      // 400: the station has nothing to follow for this field, so the backend keeps the edited value.
      const nothingToFollow =
        field === "name"
          ? "Stacioni nuk ka emër në anglisht, ndaj emri mbetet i ndryshuar"
          : "Stacioni nuk ka koordinata, ndaj vendndodhja mbetet e ndryshuar";

      if (response.status !== 200) {
        const message = response.status === 400 ? nothingToFollow : "Marrja nga stacioni dështoi";
        toast.error(message, { duration: 3500 });
        return;
      }

      const station = response.data;
      setFlags({ isNameEdited: !!station.isNameEdited, isLocationEdited: !!station.isLocationEdited });

      const stillEdited = field === "name" ? station.isNameEdited : station.isLocationEdited;
      if (stillEdited) {
        toast.error(nothingToFollow, { duration: 3500 });
        return;
      }

      if (field === "name") {
        resetField("name", { defaultValue: station.name });
        toast.success("Emri ndjek përsëri stacionin");
      } else {
        resetField("latitude", { defaultValue: station.latitude });
        resetField("longitude", { defaultValue: station.longitude });
        toast.success("Vendndodhja ndjek përsëri stacionin");
      }
    } catch (error) {
      toast.error("Marrja nga stacioni dështoi");
      console.error("Error resetting station:", error);
    } finally {
      setIsResetting(false);
    }
  };

  const onSubmit = async (data: IGTFSStation) => {
    try {
      const response = await updateGTFSStation({ ...data, id: Number(id) });

      if (response.status !== 200 && response.status !== 201) {
        toast.error((response as any).message, { duration: 3500 });
        return;
      }

      toast.success("Stacioni u ndryshua me sukses");
      setTimeout(() => {
        navigate("/gtfs/stations", { state: ["Menaxhimi i GTFS", "Stacionet"] });
      }, 500);

    } catch (error) {
      toast.error("Ndryshimi i stacionit dështoi. Ju lutem kontrolloni të dhënat dhe provoni përsëri.");
      console.error("Error updating station:", error);
    }
  };

  return (
    <>
      <Link
        to="/gtfs/stations"
        state={["Menaxhimi i GTFS", "Stacionet"]}
        className="flex items-center mb-4 text-primary"
      >
        <ChevronLeftIcon className="w-4 h-4 mr-2" />
        Kthehu tek lista
      </Link>
      <FormWrapper
         history={{ entityName: "GTFSStop", entityId: id }}
         showPrompt={
          !isSubmitSuccessful &&
          !!Object.keys(dirtyFields).length
        }
        title="Ndrysho GTFS Stacionin"
        onSubmit={handleSubmit(onSubmit)}>
        {isLoading || isSubmitting ? (
          <div className="flex justify-center items-center py-8">
            <SpinnerLoader />
          </div>
        ) : (
          <div className="grid gap-6">
            <Input
              label="Emri*"
              placeholder="Shkruani emrin"
              error={errors.name?.message}
              {...register("name")}
            />
            <Input
              label="Gjerësia*"
              type="number"
              step="any"
              placeholder="Shkruani gjerësinë gjeografike të stacionit të nisjes"
              error={errors.latitude?.message}
              {...register("latitude")}
            />
            <Input
              label="Gjatësia*"
              type="number"
              step="any"
              placeholder="Shkruani gjatësinë gjeografike të stacionit të nisjes"
              error={errors.longitude?.message}
              {...register("longitude")}
            />
            <div className="grid gap-3 text-sm">
              <div className="flex items-center justify-between gap-4">
                <span className="flex items-center gap-1.5">
                  Emri: {flags.isNameEdited ? "i ndryshuar për GTFS" : "ndjek stacionin"}
                  <InfoTip text={STATION_SOURCE_TIP} />
                </span>
                {flags.isNameEdited && (
                  <Button
                    btnType="custom"
                    className="border border-primary text-primary"
                    disabled={isResetting}
                    onClick={() => void onResetToStation("name")}
                  >
                    Merr emrin nga stacioni
                  </Button>
                )}
              </div>
              <div className="flex items-center justify-between gap-4">
                <span>
                  Vendndodhja: {flags.isLocationEdited ? "e ndryshuar për GTFS" : "ndjek stacionin"}
                </span>
                {flags.isLocationEdited && (
                  <Button
                    btnType="custom"
                    className="border border-primary text-primary"
                    disabled={isResetting}
                    onClick={() => void onResetToStation("location")}
                  >
                    Merr vendndodhjen nga stacioni
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </FormWrapper>
    </>
  );
};

export default EditGTFSStation;
